/**
 * Analytics Table Component for v0.6.0
 * Displays detailed analytics data in tabular format
 */

import type { MemberAnalytics } from '../../../types';

interface AnalyticsTableProps {
  analytics: MemberAnalytics;
  className?: string;
}

const AnalyticsTable = ({ analytics, className = '' }: AnalyticsTableProps) => {
  return (
    <div className={`analytics-table ${className}`}>
      {/* Overview Cards */}
      <div className="analytics-table__overview">
        <div className="analytics-table__card">
          <div className="analytics-table__card-label">總廣告數</div>
          <div className="analytics-table__card-value">{analytics.ads.total}</div>
        </div>
        <div className="analytics-table__card">
          <div className="analytics-table__card-label">總聯繫數</div>
          <div className="analytics-table__card-value">{analytics.contacts.total}</div>
        </div>
        <div className="analytics-table__card">
          <div className="analytics-table__card-label">平均點擊率</div>
          <div className="analytics-table__card-value">
            {analytics.ads.topViewed.length > 0
              ? (analytics.ads.topViewed.reduce((sum, ad) => sum + ad.ctr, 0) / analytics.ads.topViewed.length).toFixed(1) + '%'
              : '0%'}
          </div>
        </div>
      </div>

      {/* Ads by Status Table */}
      <div className="analytics-table__section">
        <h3 className="analytics-table__section-title">廣告狀態</h3>
        <table className="analytics-table__table">
          <thead>
            <tr>
              <th>狀態</th>
              <th>數量</th>
            </tr>
          </thead>
          <tbody>
            {Object.entries(analytics.ads.byStatus).map(([status, count]) => (
              <tr key={status}>
                <td>
                  <span className={`status-badge status-badge--${status}`}>
                    {status === 'active' ? '已上架' : status === 'inactive' ? '已下架' : status === 'pending' ? '待審核' : status}
                  </span>
                </td>
                <td>{count}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Top Viewed Ads Table */}
      <div className="analytics-table__section">
        <h3 className="analytics-table__section-title">熱門廣告 (瀏覽)</h3>
        <table className="analytics-table__table">
          <thead>
            <tr>
              <th>標題</th>
              <th>瀏覽</th>
              <th>點擊</th>
              <th>點擊率</th>
            </tr>
          </thead>
          <tbody>
            {analytics.ads.topViewed.length > 0 ? analytics.ads.topViewed.map((ad) => (
              <tr key={ad.adId}>
                <td className="analytics-table__title-cell">{ad.title}</td>
                <td>{ad.views}</td>
                <td>{ad.clicks}</td>
                <td>{ad.ctr.toFixed(2)}%</td>
              </tr>
            )) : (
              <tr>
                <td colSpan={4} className="analytics-table__empty">尚無資料</td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Recent Contacts Table */}
      <div className="analytics-table__section">
        <h3 className="analytics-table__section-title">最近聯繫</h3>
        <table className="analytics-table__table">
          <thead>
            <tr>
              <th>需求標題</th>
              <th>聯繫數</th>
              <th>日期</th>
            </tr>
          </thead>
          <tbody>
            {analytics.contacts.recent.length > 0 ? analytics.contacts.recent.map((contact) => (
              <tr key={contact.wishId}>
                <td className="analytics-table__title-cell">{contact.title}</td>
                <td>{contact.contacts}</td>
                <td>{new Date(contact.date).toLocaleDateString('zh-TW')}</td>
              </tr>
            )) : (
              <tr>
                <td colSpan={3} className="analytics-table__empty">尚無資料</td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default AnalyticsTable;
